// src/pages/Blog.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock, Plus, X, LogOut, Eye, EyeOff, Lock } from "lucide-react";
import { blogs as staticBlogs, blogCategories } from "../data/mock";
import { blogsData } from "../data/blogsData";
import { supabase, SUPABASE_READY } from "../utils/supabase";
import { useAdminAuth } from "../hooks/useAdminAuth";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
};

// Merge static blogs with DB blogs (DB takes priority by slug)
const mergeBlogs = (staticList, dbList) => {
  const dbSlugs = new Set(dbList.map((b) => b.slug || b.id));
  const filtered = staticList.filter((b) => !dbSlugs.has(b.id));
  return [
    ...dbList.map((b) => ({
      ...b,
      id: b.slug || b.id,
      readTime: b.read_time || b.readTime,
      heroImage: b.hero_image || b.heroImage,
    })),
    ...filtered,
  ].sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
};

// ── Admin Login Modal ─────────────────────────────────────────────────────────
const AdminLoginModal = ({ onClose, onSuccess }) => {
  const { login, error, loading } = useAdminAuth();
  const [pw, setPw]   = useState("");
  const [show, setShow] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await login(pw);
    if (ok) onSuccess();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="relative border hairline rounded-md bg-background w-full max-w-sm mx-4 p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-full border hairline grid place-items-center">
            <Lock size={14} />
          </div>
          <div>
            <p className="text-sm font-medium">Admin Access</p>
            <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              Portfolio CMS
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              placeholder="Enter password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              className="w-full bg-transparent border hairline rounded-md px-4 pr-10 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30 transition-all"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>

          {error && (
            <p className="font-mono text-[11px] text-red-500">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !pw.trim()}
            className="w-full py-3 bg-foreground text-background text-sm font-medium rounded-md hover:opacity-80 transition-opacity disabled:opacity-40"
          >
            {loading ? "Verifying…" : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Blog page ─────────────────────────────────────────────────────────────────
const Blog = () => {
  const { isAdmin, logout } = useAdminAuth();
  const [cat, setCat]             = useState("All");
  const [q, setQ]                 = useState("q");
  const [q2, setQ2]               = useState("");
  const [dbBlogs, setDbBlogs]     = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  // Secret trigger: click the "05 — Journal" label 5 times fast
  const handleSecretClick = useCallback(() => {
    setClickCount((n) => {
      const next = n + 1;
      if (next >= 5) {
        if (!isAdmin) setShowLogin(true);
        return 0;
      }
      setTimeout(() => setClickCount(0), 2000);
      return next;
    });
  }, [isAdmin]);

  // Load DB blogs
  useEffect(() => {
    if (!SUPABASE_READY) return;
    supabase
      .from("blog_posts")
      .select("*")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setDbBlogs(data); });
  }, []);

  const allBlogs = useMemo(() => mergeBlogs(staticBlogs, dbBlogs), [dbBlogs]);

  const allCategories = useMemo(() => {
    const cats = new Set(allBlogs.map((b) => b.category).filter(Boolean));
    return ["All", ...Array.from(cats)];
  }, [allBlogs]);

  const filtered = useMemo(() => {
    return allBlogs
      .filter((b) => cat === "All" ? true : b.category === cat)
      .filter((b) => q2.trim() === ""
        ? true
        : (b.title + (b.excerpt || "")).toLowerCase().includes(q2.trim().toLowerCase())
      );
  }, [allBlogs, cat, q2]);

  const [featured, ...rest] = filtered.length ? filtered : allBlogs;

  return (
    <div>
      {/* Login modal */}
      {showLogin && (
        <AdminLoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={() => setShowLogin(false)}
        />
      )}

      {/* ── Hero ── */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-16 md:pb-20">
          <div
            className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5 cursor-default select-none"
            onClick={handleSecretClick}
            title=""
          >
            05 — Journal
          </div>
          <div className="grid grid-cols-12 gap-6 items-end">
            <h1 className="col-span-12 md:col-span-8 display-xl text-5xl md:text-8xl leading-[0.95]">
              Notes from the <span className="italic font-light">backend.</span>
            </h1>
            <div className="col-span-12 md:col-span-4 flex items-center gap-3">
              <input
                value={q2}
                onChange={(e) => setQ2(e.target.value)}
                placeholder="Search posts…"
                className="flex-1 bg-transparent border-b hairline py-3 px-1 outline-none focus:border-foreground transition-colors text-sm"
              />
              {/* Admin controls — only visible when logged in */}
              {isAdmin && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to="/journal/new"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-foreground text-background text-xs font-mono uppercase tracking-widest rounded-md hover:opacity-80 transition-opacity"
                  >
                    <Plus size={12} /> New Post
                  </Link>
                  <button
                    onClick={logout}
                    className="p-2 border hairline rounded-md text-muted-foreground hover:text-foreground transition-colors"
                    title="Logout"
                  >
                    <LogOut size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-10">
            {allCategories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 border hairline rounded-full text-sm transition-colors ${
                  cat === c ? "bg-foreground text-background border-foreground" : "hover:bg-muted"
                }`}
                data-cursor-hover
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured ── */}
      {featured && (
        <section className="border-b hairline">
          <Link to={`/journal/${featured.id || featured.slug}`} className="group block" data-cursor-hover>
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-1 font-mono text-xs text-muted-foreground">01</div>
              <div className="col-span-12 md:col-span-7">
                <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-4 flex items-center gap-4">
                  <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {formatDate(featured.date || featured.created_at)}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {featured.readTime || featured.read_time}</span>
                  <span>— {featured.category}</span>
                </div>
                <h2 className="display-xl text-4xl md:text-6xl leading-[1] group-hover:translate-x-1 transition-transform">
                  {featured.title}
                </h2>
                <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl">
                  {featured.excerpt}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(featured.tags || []).map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border hairline rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-8 inline-flex items-center gap-2 font-medium link-grow">
                  Read article <ArrowUpRight size={16} />
                </div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="aspect-[4/5] w-full border hairline rounded-md relative overflow-hidden bg-muted/40">
                  {featured.heroImage?.src || featured.hero_image?.src ? (
                    <img
                      src={featured.heroImage?.src || featured.hero_image?.src}
                      alt={featured.heroImage?.alt || ""}
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 grid-bg" />
                      <div className="absolute inset-0 grid place-items-center">
                        <div className="font-display text-[8rem] leading-none opacity-80">
                          {(featured.category || "B")[0]}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* ── Rest ── */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4">
          {filtered.length === 0 && (
            <div className="py-24 text-center text-muted-foreground">
              No posts match your search.
            </div>
          )}
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
          {rest.map((b, i) => (
            <Link
              to={`/journal/${b.id || b.slug}`}
              key={b.id || b.slug}
              className="group grid grid-cols-12 gap-6 border-t hairline py-8 md:py-10 items-baseline"
              data-cursor-hover
            >
              <div className="col-span-12 md:col-span-1 font-mono text-xs text-muted-foreground">
                0{i + 2}
              </div>
              <div className="col-span-12 md:col-span-7">
                <h3 className="font-display text-2xl md:text-4xl leading-tight group-hover:translate-x-1 transition-transform">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                  {b.excerpt}
                </p>
              </div>
              <div className="col-span-6 md:col-span-2 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                {formatDate(b.date || b.created_at)}
              </div>
              <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-2 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                {b.readTime || b.read_time} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
          <div className="border-t hairline" />
        </div>
      </section>
    </div>
  );
};

export default Blog;